import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

function getAutoTheme() {
  const hour = new Date().getHours();
  // Day: 7am to 7pm, Night: 7pm to 7am
  return (hour >= 7 && hour < 19) ? 'light' : 'dark';
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('ov-theme-mode');
    if (saved === 'light' || saved === 'dark') return saved;
    return getAutoTheme(); // auto by default
  });
  const [autoMode, setAutoMode] = useState(() => {
    return localStorage.getItem('ov-theme-auto') !== 'false';
  });

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    if (mode === 'light') {
      root.classList.add('light');
      root.classList.remove('dark');
    } else {
      root.classList.add('dark');
      root.classList.remove('light');
    }
    // Remove any old cloud overlay from previous implementation
    const old = document.getElementById('cloud-overlay');
    if (old) old.remove();
  }, [mode]);

  // Auto-switch based on time of day
  useEffect(() => {
    if (!autoMode) return;
    const check = () => {
      const newTheme = getAutoTheme();
      setMode(newTheme);
      localStorage.setItem('ov-theme-mode', newTheme);
    };
    check();
    // iter_474 — Skip the per-minute auto-theme check while the tab
    // is hidden. We also re-run once on visibilitychange to catch
    // sunrise/sunset crossings the user missed.
    const interval = setInterval(() => {
      if (document.hidden) return;
      check();
    }, 60000);
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [autoMode]);

  const toggleTheme = useCallback(() => {
    setMode(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('ov-theme-mode', next);
      localStorage.setItem('ov-theme-auto', 'false');
      setAutoMode(false);
      return next;
    });
  }, []);

  const enableAutoTheme = useCallback(() => {
    setAutoMode(true);
    localStorage.setItem('ov-theme-auto', 'true');
    const theme = getAutoTheme();
    setMode(theme);
    localStorage.setItem('ov-theme-mode', theme);
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, autoMode, enableAutoTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
